import { useCallback } from "react";

/**
 * Puts native input attributes back on a `SkyInput`.
 *
 * `SkyInputProps` is a closed list — the component does not spread unknown
 * props onto the element it renders — so anything not in that list is dropped
 * without warning. `inputMode`, `min`, `max` and `autoFocus` all fall outside
 * it, and so does every `aria-*` attribute (those type-check anyway, because
 * hyphenated JSX attributes skip excess-property checking, which is what makes
 * the loss silent).
 *
 * `inputMode` is the one that matters most here. It is what shows a phone a
 * decimal keypad instead of a full keyboard, and most of the places it is used
 * in skyEmployee are money fields — a gift card's value, a budget — typed by
 * people standing up, on a phone, in a care home. Losing it is not cosmetic.
 *
 * Applied through the forwarded ref rather than a prop, as a callback ref so it
 * also catches an input that mounts later (one inside a dialog, or behind a
 * toggle). `useNativeInputAttrs.test.ts` pins each attribute, so if a future
 * version of the library stops forwarding its ref to the input, that fails
 * instead of a keypad quietly disappearing.
 *
 * Prefer a real prop where one exists: `hint` describes a field (don't hand-roll
 * `aria-describedby`), and `validationState` / `validationMessage` report a
 * problem with it.
 */
export interface NativeInputAttrs {
  /** Which on-screen keyboard a touch device offers. */
  inputMode?: "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url" | "none";
  min?: number | string;
  max?: number | string;
  step?: number | string;
  /** Focus on mount — for the field a dialog opens onto. */
  autoFocus?: boolean;
}

export function useNativeInputAttrs<T extends HTMLInputElement | HTMLTextAreaElement>(
  attrs: NativeInputAttrs,
) {
  const { inputMode, min, max, step, autoFocus } = attrs;
  return useCallback(
    (node: T | null) => {
      if (!node) return;
      if (inputMode) node.inputMode = inputMode;
      if (min !== undefined) node.setAttribute("min", String(min));
      if (max !== undefined) node.setAttribute("max", String(max));
      if (step !== undefined) node.setAttribute("step", String(step));
      // Focus after the element is in the document, which a callback ref
      // guarantees; `autoFocus` as a React prop would have been dropped.
      if (autoFocus) node.focus();
    },
    [inputMode, min, max, step, autoFocus],
  );
}
